// client/src/components/pages/TelegramBI/TemplateManager/index.tsx (converted from index.js, ST-00259)
//
// ST-00259: toasts now render (AppToaster), and the list reloads on every keystroke in the
// search box and twice when a filter change also resets the page, so the "Failed to load
// templates" toast carries a stable id: a failing endpoint shows it once, not once per request.
import { useState, useEffect, useCallback, type ComponentType } from 'react';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaCopy,
  FaEye,
  FaCode,
  FaFileAlt,
  FaPlay,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
  FaDatabase,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../../../services/api';
import { PageHeader, ThemedSelect } from '../../../../types/commonComponents';
import type { HttpErrorLike } from '../../../../types/clientShell';
import type {
  TelegramEnvelope,
  TelegramTemplateCopy,
  TelegramTemplateForm,
  TelegramTemplateListPage,
  TelegramTemplateListParams,
  TelegramTemplatePreviewRequest,
  TelegramTemplatePreviewResult,
  TelegramTemplateRow,
  TelegramTemplateType,
  TelegramVisibilityFilter,
} from '../../../../types/clientPages/telegramBI';
import TemplateEditorJs from './TemplateEditor';
import TemplatePreviewJs from './TemplatePreview';
import CreateTemplateModalJs from './CreateTemplateModal';
import EditTemplateModalJs from './EditTemplateModal';
import TemplateVariablesModalJs from './TemplateVariablesModal';
import { formatRelativeTime } from '../../../../utils/dateUtils';

/** The list toast's id: one "Failed to load templates" on screen however often the list reloads. */
export const TEMPLATES_LOAD_TOAST_ID = 'telegram-templates-load';

const PAGE_SIZE = 20;

/** The type filter: every type, or one of them. */
type TemplateTypeFilter = 'all' | TelegramTemplateType;

// Typed views of the (still JavaScript) children, with the props this page passes.
const TemplateEditor = TemplateEditorJs as unknown as ComponentType<{
  template: TelegramTemplateRow;
  readOnly?: boolean;
}>;
const TemplatePreview = TemplatePreviewJs as unknown as ComponentType<{
  template: TelegramTemplateRow;
  previewData: TelegramTemplatePreviewResult;
  onRefresh: () => void;
}>;
const CreateTemplateModal = CreateTemplateModalJs as unknown as ComponentType<{
  onClose: () => void;
  onSubmit: (data: TelegramTemplateForm) => Promise<void>;
}>;
const EditTemplateModal = EditTemplateModalJs as unknown as ComponentType<{
  template: TelegramTemplateRow;
  onClose: () => void;
  onSubmit: (data: TelegramTemplateForm) => Promise<void>;
}>;
const TemplateVariablesModal = TemplateVariablesModalJs as unknown as ComponentType<{
  template: TelegramTemplateRow;
  onClose: () => void;
}>;

/** `{ success, data }` → data; a bare payload is passed through. */
function payloadOf<T>(body: unknown): T | undefined {
  return (body as TelegramEnvelope<T> | undefined)?.data || (body as T | undefined);
}

/** The server's words for a failed request (`error` / `message`). */
const apiError = (err: unknown) => (err as HttpErrorLike | null | undefined)?.response?.data;

interface TypeBadge {
  label: string;
  color: string;
}

const TYPE_CONFIG: Record<TelegramTemplateType, TypeBadge> = {
  text: { label: 'Text', color: 'bg-gray-100 text-gray-600' },
  markdown: { label: 'MD', color: 'bg-blue-50 text-blue-700' },
  html: { label: 'HTML', color: 'bg-amber-50 text-amber-700' },
};

const typeBadge = (type: string | undefined): TypeBadge =>
  (type ? (TYPE_CONFIG as Record<string, TypeBadge | undefined>)[type] : undefined) ||
  TYPE_CONFIG.text;

const TemplateManager = () => {
  const [templates, setTemplates] = useState<TelegramTemplateRow[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<TelegramTemplateRow | null>(null);
  const [previewData, setPreviewData] = useState<TelegramTemplatePreviewResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showVariablesModal, setShowVariablesModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<TelegramVisibilityFilter>('all');
  const [templateTypeFilter, setTemplateTypeFilter] = useState<TemplateTypeFilter>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const params: TelegramTemplateListParams = {
        page: currentPage,
        limit: PAGE_SIZE,
        search: searchTerm,
      };
      if (filterType === 'public') params.isPublic = true;
      if (filterType === 'private') params.isPublic = false;
      if (templateTypeFilter !== 'all') params.type = templateTypeFilter;

      const res = await api.telegramBI.templates.getAll(params);
      const data = payloadOf<TelegramTemplateListPage>(res.data);
      setTemplates(data?.templates || []);
      const total = data?.totalCount || data?.total || 0;
      setTotalCount(total);
      setTotalPages(Math.ceil(total / PAGE_SIZE) || 1);
    } catch {
      toast.error('Failed to load templates', { toastId: TEMPLATES_LOAD_TOAST_ID });
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, filterType, templateTypeFilter]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterType, templateTypeFilter]);

  const selectTemplate = async (template: TelegramTemplateRow) => {
    try {
      const res = await api.telegramBI.templates.get(template.template_id);
      setSelectedTemplate(payloadOf<TelegramTemplateRow>(res.data) || template);
    } catch {
      setSelectedTemplate(template);
    }
    setPreviewData(null);
  };

  const previewTemplate = async (template: TelegramTemplateRow) => {
    try {
      setPreviewing(true);
      const sampleData: TelegramTemplatePreviewRequest = {
        data: [
          { Name: 'Sample Item', Value: 42, Status: 'active' },
          { Name: 'Another Item', Value: 15, Status: 'pending' },
        ],
        meta: {
          templateName: template.template_name,
          rowCount: 2,
          generatedAt: new Date(),
          executionTime: 150,
        },
      };
      const res = await api.telegramBI.templates.preview(template.template_id, sampleData);
      setPreviewData(payloadOf<TelegramTemplatePreviewResult>(res.data) ?? null);
      setSelectedTemplate(template);
    } catch (err) {
      toast.error(apiError(err)?.error || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const createTemplate = async (data: TelegramTemplateForm) => {
    await api.telegramBI.templates.create(data);
    toast.success('Template created');
    setShowCreateModal(false);
    loadTemplates();
  };

  const updateTemplate = async (data: TelegramTemplateForm) => {
    if (!selectedTemplate) return;
    await api.telegramBI.templates.update(selectedTemplate.template_id, data);
    toast.success('Template updated');
    setShowEditModal(false);
    loadTemplates();
  };

  const deleteTemplate = async (id: number) => {
    if (!window.confirm('Delete this template? This cannot be undone.')) return;
    try {
      await api.telegramBI.templates.delete(id);
      toast.success('Template deleted');
      if (selectedTemplate?.template_id === id) {
        setSelectedTemplate(null);
        setPreviewData(null);
      }
      loadTemplates();
    } catch (err) {
      toast.error(apiError(err)?.message || 'Failed to delete');
    }
  };

  const duplicateTemplate = async (template: TelegramTemplateRow) => {
    try {
      const data: TelegramTemplateCopy = {
        templateName: `${template.template_name} (Copy)`,
        templateContent: template.template_content,
        templateType: template.template_type,
        templateDescription: template.template_description,
        isPublic: false,
      };
      await api.telegramBI.templates.create(data);
      toast.success('Template duplicated');
      loadTemplates();
    } catch {
      toast.error('Failed to duplicate');
    }
  };

  const formatDate = (d: string | null | undefined) => {
    if (!d) return '—';
    return formatRelativeTime(d) || '—';
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        icon={FaFileAlt}
        title="Template Manager"
        subtitle="Design Handlebars message templates for Telegram delivery"
        actions={
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            aria-label="New Template"
            title="New Template"
            className="inline-flex items-center justify-center gap-2 h-10 px-3.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium whitespace-nowrap transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
          >
            <FaPlus className="w-3.5 h-3.5" aria-hidden="true" />
            <span>
              New<span className="page-header__label"> Template</span>
            </span>
          </button>
        }
      />

      <div className="p-3 sm:p-6 max-w-[1800px] mx-auto">
        {/* Toolbar */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-md">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-surface focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all"
            />
          </div>
          <ThemedSelect
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as TelegramVisibilityFilter)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-surface focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none"
          >
            <option value="all">All</option>
            <option value="public">Public</option>
            <option value="private">Private</option>
          </ThemedSelect>
          <ThemedSelect
            value={templateTypeFilter}
            onChange={(e) => setTemplateTypeFilter(e.target.value as TemplateTypeFilter)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-surface focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none"
          >
            <option value="all">All Types</option>
            <option value="text">Text</option>
            <option value="markdown">Markdown</option>
            <option value="html">HTML</option>
          </ThemedSelect>
          <span className="text-xs text-gray-400">{totalCount} total</span>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-5">
          {/* Template List */}
          <div
            className="xl:col-span-2 bg-surface rounded-lg border border-gray-200 flex flex-col"
            style={{ maxHeight: 'calc(100vh - 240px)' }}
          >
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <FaCode className="text-primary-500 text-xs" />
                Templates
              </h2>
              <span className="text-xs text-gray-400">{templates.length} shown</span>
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
              {loading ? (
                <div className="flex items-center justify-center h-48">
                  <div className="w-6 h-6 border-2 border-gray-300 border-t-primary-500 rounded-full animate-spin" />
                </div>
              ) : templates.length === 0 ? (
                <div className="p-8 text-center">
                  <FaFileAlt className="mx-auto text-2xl text-gray-300 mb-2" />
                  <p className="text-sm text-gray-500">No templates found</p>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="mt-2 text-xs text-primary-600 font-medium"
                  >
                    Create your first template
                  </button>
                </div>
              ) : (
                templates.map((tpl) => {
                  const tc = typeBadge(tpl.template_type);
                  return (
                    <div
                      key={tpl.template_id}
                      onClick={() => selectTemplate(tpl)}
                      className={`px-4 py-3 cursor-pointer transition-all hover:bg-gray-50/70 ${selectedTemplate?.template_id === tpl.template_id ? 'bg-primary-50/50 border-l-2 border-primary-500' : 'border-l-2 border-transparent'}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-sm font-medium text-gray-900 truncate">
                              {tpl.template_name}
                            </span>
                            <span
                              className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${tc.color}`}
                            >
                              {tc.label}
                            </span>
                          </div>
                          {tpl.template_description && (
                            <div className="text-xs text-gray-500 truncate">
                              {tpl.template_description}
                            </div>
                          )}
                          <div className="flex items-center gap-3 mt-1.5">
                            <span
                              className={`text-[10px] px-1.5 py-0.5 rounded ${tpl.is_public ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500'}`}
                            >
                              {tpl.is_public ? 'Public' : 'Private'}
                            </span>
                            <span className="text-[11px] text-gray-400">
                              {tpl.usage_count || 0} uses
                            </span>
                            <span className="text-[11px] text-gray-400">
                              {formatDate(tpl.updated_at || tpl.created_at)}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              previewTemplate(tpl);
                            }}
                            disabled={previewing}
                            className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-50 disabled:opacity-40 transition-colors"
                            title="Preview"
                          >
                            <FaPlay className="text-[10px]" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              selectTemplate(tpl);
                              setShowEditModal(true);
                            }}
                            className="p-1.5 rounded-md text-primary-600 hover:bg-primary-50 transition-colors"
                            title="Edit"
                          >
                            <FaEdit className="text-[10px]" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              duplicateTemplate(tpl);
                            }}
                            className="p-1.5 rounded-md text-violet-600 hover:bg-violet-50 transition-colors"
                            title="Duplicate"
                          >
                            <FaCopy className="text-[10px]" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteTemplate(tpl.template_id);
                            }}
                            className="p-1.5 rounded-md text-red-500 hover:bg-red-50 transition-colors"
                            title="Delete"
                          >
                            <FaTrash className="text-[10px]" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1 rounded text-gray-400 hover:text-gray-600 disabled:opacity-30"
                >
                  <FaChevronLeft className="text-xs" />
                </button>
                <span className="text-xs text-gray-500">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1 rounded text-gray-400 hover:text-gray-600 disabled:opacity-30"
                >
                  <FaChevronRight className="text-xs" />
                </button>
              </div>
            )}
          </div>

          {/* Template Detail + Preview */}
          <div className="xl:col-span-3 space-y-4">
            {selectedTemplate ? (
              <>
                <div className="bg-surface rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <div className="flex-1 min-w-0">
                      <h2 className="text-base font-semibold text-gray-900 truncate">
                        {selectedTemplate.template_name}
                      </h2>
                      {selectedTemplate.template_description && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {selectedTemplate.template_description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => setShowVariablesModal(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <FaDatabase className="text-[10px]" />
                        Variables
                      </button>
                      <button
                        onClick={() => previewTemplate(selectedTemplate)}
                        disabled={previewing}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-500 rounded-lg disabled:opacity-50 transition-colors"
                      >
                        {previewing ? (
                          <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          <FaPlay className="text-[9px]" />
                        )}
                        {previewing ? 'Loading...' : 'Preview'}
                      </button>
                    </div>
                  </div>
                  <TemplateEditor template={selectedTemplate} readOnly={true} />
                  <div className="flex items-center gap-4 px-4 py-2 border-t border-gray-100 bg-gray-50/50">
                    {[
                      { label: 'Type', value: selectedTemplate.template_type },
                      { label: 'Uses', value: selectedTemplate.usage_count || 0 },
                      { label: 'Version', value: `v${selectedTemplate.version || 1}` },
                    ].map(({ label, value }) => (
                      <div key={label} className="text-[11px]">
                        <span className="text-gray-400">{label}: </span>
                        <span className="text-gray-600 font-medium">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {previewData && (
                  <TemplatePreview
                    template={selectedTemplate}
                    previewData={previewData}
                    onRefresh={() => previewTemplate(selectedTemplate)}
                  />
                )}
              </>
            ) : (
              <div className="bg-surface rounded-lg border border-gray-200 p-12 text-center">
                <FaEye className="mx-auto text-3xl text-gray-300 mb-3" />
                <p className="text-sm text-gray-500">Select a template to view and preview</p>
              </div>
            )}
          </div>
        </div>

        {showCreateModal && (
          <CreateTemplateModal
            onClose={() => setShowCreateModal(false)}
            onSubmit={createTemplate}
          />
        )}
        {showEditModal && selectedTemplate && (
          <EditTemplateModal
            template={selectedTemplate}
            onClose={() => setShowEditModal(false)}
            onSubmit={updateTemplate}
          />
        )}
        {showVariablesModal && selectedTemplate && (
          <TemplateVariablesModal
            template={selectedTemplate}
            onClose={() => setShowVariablesModal(false)}
          />
        )}
      </div>
    </div>
  );
};

export default TemplateManager;
